import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

// Retained for back-compat with call sites that still pass `max`; the value
// is now ignored. Truncation is CSS-driven (the container width decides the
// cut), and the full string reveals inline on hover via `.snap-truncate`
// (see tokens.css) rather than through a separate portal tooltip.
export const MAX_LABEL = 15;

interface Props {
  text: string | null | undefined;
  /** @deprecated No longer used — truncation is now CSS width-based. */
  max?: number;
  suffix?: string;
  className?: string;
  /**
   * Marks a label as belonging to a newly-introduced derivedContext or skill
   * (project overlay). Applies the shared blue+bold treatment so the
   * Roadmap sub-views speak one visual language for "this is new."
   */
  isNew?: boolean;
  /**
   * Disable the inline expand-on-hover reveal. The text still truncates
   * with an ellipsis, but hovering no longer lifts it into a chip. Used
   * by the inline editors, where hover is reserved for the click-to-edit
   * highlight and the lengthening would fight it.
   */
  noReveal?: boolean;
}

/**
 * Width-clamped label. The text is rendered in full and clipped to its
 * container with a CSS ellipsis; hovering expands the inner span inline
 * (a toolbar-styled chip) to reveal the whole string — no separate
 * tooltip. The `.snap-truncate__text` wrapper is what the hover reveal
 * targets; see `.snap-truncate` in tokens.css.
 */
export function TruncatedLabel({ text, suffix, className, isNew, noReveal }: Props) {
  const full = (text ?? '') + (suffix ?? '');
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  // Only arm the hover reveal when the text is actually clipped — checked
  // on both axes (single-line ellipsis = width overflow; the 2-line
  // comp-name clamp = height overflow). A ResizeObserver re-measures when
  // the column flexes. Bounded to label/header cells (no per-data-cell
  // observers), so the count stays modest.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () =>
      setTruncated(
        el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1,
      );
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [full]);

  const classes = [
    'snap-truncate',
    truncated ? 'is-truncated' : null,
    noReveal ? 'snap-truncate--no-reveal' : null,
    className,
    isNew ? 'snap-grid__label--new' : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span ref={ref} className={classes}>
      <span className='snap-truncate__text'>{full}</span>
    </span>
  );
}

// Tree-connector variant for the muted column: which segment shape this
// skill row needs based on its position within its derivedContext.
export type NestVariant = 'first' | 'middle' | 'last' | 'single';

export function nestVariant(skillIndex: number, skillCount: number): NestVariant {
  if (skillCount <= 1) return 'single';
  if (skillIndex === 0) return 'first';
  if (skillIndex === skillCount - 1) return 'last';
  return 'middle';
}

export function NestConnector({ variant }: { variant: NestVariant }) {
  return (
    <span
      className={`snap-grid__nest snap-grid__nest--${variant}`}
      aria-hidden='true'
    />
  );
}

/**
 * A flat/expanded skill row's connector cell. The tree hairline, and — when
 * `onToggle` is supplied — a click target that collapses the derivedContext back
 * to a single grouped band (the expand/collapse affordance the /setup editor
 * and the grouped band's connector already use). Kept in sync across Timeline,
 * Resilience, and Talent so the connector reads the same everywhere. `frozen`
 * pins it left in the sticky views; `style` carries the per-cell freeze offset
 * (and any grid coordinates).
 */
export function ConnectorCell({
  variant,
  onToggle,
  frozen = false,
  style,
}: {
  variant: NestVariant;
  /** When set, the connector becomes a button that collapses the group. */
  onToggle?: () => void;
  frozen?: boolean;
  style?: CSSProperties;
}) {
  const className = [
    'snap-grid__row-label',
    'snap-grid__row-label--conn',
    frozen ? 'is-frozen' : null,
    onToggle ? 'snap-grid__row-label--conn-toggle' : null,
  ]
    .filter(Boolean)
    .join(' ');
  if (!onToggle) {
    return (
      <div className={className} style={style}>
        <NestConnector variant={variant} />
      </div>
    );
  }
  return (
    <div
      className={className}
      style={style}
      role='button'
      tabIndex={0}
      aria-expanded
      aria-label='Group skills'
      onClick={onToggle}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <NestConnector variant={variant} />
    </div>
  );
}

interface GroupedCompLabelProps {
  /** DerivedContext-name node for col 1 — each view supplies its own widget
   *  (TruncatedLabel, EditableLabel, …). */
  name: ReactNode;
  /** Skill-count node for col 4 — plain "N skills" or a view-specific
   *  variant (Timeline's change delta, Roadmap's "(+N added)"). */
  count: ReactNode;
  /** How many of the derivedContext's skills are institutional/proprietary;
   *  drives the "N proprietary" callout below the count. 0 hides it. */
  propCount: number;
  /** Extra class on the name cell (e.g. Timeline's new/deprecated diff
   *  treatment via `snap-grid__row-label--new` / `--deprecated`). */
  nameClassName?: string;
  /**
   * Drop the "N proprietary" line under the skill count, keeping the green dot.
   * Set in ultra-compact, where a row can be 15px and has no second line.
   */
  hidePropCount?: boolean;
  /** Pin the four columns to the left edge (Talent / Roadmap-Resource). */
  frozen?: boolean;
  /** Sticky left offsets for the four columns when `frozen`. */
  freezeLefts?: readonly number[];
  /** Explicit grid row for grids that place cells by coordinate rather
   *  than auto-flow (Roadmap-Resource). When set, each cell also gets its
   *  explicit `gridColumn` (1–4). */
  gridRow?: number;
  /** When provided, the connector hairline becomes a clickable toggle
   *  (group ↔ ungroup the derivedContext's skills). `expanded` drives the
   *  aria + tooltip wording. */
  onToggle?: () => void;
  expanded?: boolean;
}

/**
 * Grouped-view derivedContext label band. Lays the derivedContext name, a single
 * connector hairline, and the skill count across the same four leading
 * columns the flat (ungrouped) view uses — instead of stacking the name
 * over the count — so both layouts share one structure. DerivedContexts whose
 * underlying skills include institutional knowledge get a "N proprietary"
 * callout (green dot + count) beneath the skill count.
 *
 * Shared by Talent, Resilience, Timeline, and the Roadmap grids; `name` /
 * `count` are slots so each view keeps its own label widget and count text.
 */
export function GroupedCompLabel({
  name,
  count,
  propCount,
  hidePropCount = false,
  nameClassName,
  frozen = false,
  freezeLefts,
  gridRow,
  onToggle,
  expanded,
}: GroupedCompLabelProps) {
  const cellStyle = (col: number): CSSProperties | undefined => {
    const s: CSSProperties = {};
    if (freezeLefts) s.left = freezeLefts[col];
    if (gridRow != null) {
      s.gridRow = gridRow;
      s.gridColumn = col + 1;
    }
    return Object.keys(s).length > 0 ? s : undefined;
  };
  const fz = frozen ? ' is-frozen' : '';
  return (
    <>
      <div
        className={`snap-grid__row-label snap-grid__row-label--muted${fz}${
          nameClassName ? ' ' + nameClassName : ''
        }`}
        style={cellStyle(0)}
      >
        {name}
      </div>
      <div
        className={`snap-grid__row-label snap-grid__row-label--conn${fz}${
          onToggle ? ' snap-grid__row-label--conn-toggle' : ''
        }`}
        style={cellStyle(1)}
        {...(onToggle
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': expanded ?? false,
              'aria-label': expanded ? 'Group skills' : 'Ungroup skills',
              onClick: onToggle,
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle();
                }
              },
            }
          : {})}
      >
        <NestConnector variant='single' />
      </div>
      <div
        className={`snap-grid__row-label snap-grid__row-label--prop${fz}`}
        style={cellStyle(2)}
      >
        {/* Dot lives in the proprietary column (aligned with the flat
            view's prop marker) so the "N proprietary" text in col 4 can sit
            flush-left under the skill count rather than indented past it. */}
        {propCount > 0 ? (
          <span className='snap-grid__group-prop-dot' aria-hidden='true' />
        ) : null}
      </div>
      <div
        className={`snap-grid__row-label snap-grid__row-label--group-meta${
          frozen ? ' is-frozen is-frozen-edge' : ''
        }`}
        style={cellStyle(3)}
      >
        <span>{count}</span>
        {/* The "N proprietary" second line is dropped in ultra-compact — an S
            row is 15px, which cannot carry two lines of text. The green dot in
            column 3 above still marks the row, so the signal survives at the
            same position; only the redundant count text goes. */}
        {propCount > 0 && !hidePropCount ? (
          <span className='snap-grid__group-prop'>{propCount} proprietary</span>
        ) : null}
      </div>
    </>
  );
}

/**
 * Column header for the matrix grid: business-function name, with the
 * function's short description revealed on hover. Shared across Resilience /
 * Timeline / Talent / Roadmap so the header reads identically on every map.
 *
 * THE NAME GETS BOTH LINES. The header cell has always been two lines tall,
 * and the second one used to carry a permanent subtitle — or, on a rolled-up
 * column, an "N functions" child count. Neither is a label: the count counted
 * rows the collapsed matrix does not draw, and a clamped sentence of prose
 * under every column turned the axis into a wall of grey text. So the name now
 * wraps into that line (clamped at two, ellipsis past it) and the description
 * hangs below the header on hover — one gesture away, costing the axis nothing
 * at rest.
 *
 * The reveal is pure CSS (`.snap-grid__col-header__desc`), so it needs no
 * tooltip provider and works in every grid that renders this header.
 */
export function ColumnHeader({
  display,
  subtitle,
  className,
  tag,
  note,
}: {
  display: string;
  subtitle?: string | null;
  /** Extra class on the header cell — the Timeline's new / deprecated /
   *  planned diff modifiers. */
  className?: string;
  /** Badge rendered under the name (the Timeline's "planned" lifecycle tag).
   *  Short by contract: anything sentence-length belongs in `note`. */
  tag?: ReactNode;
  /** Extra line inside the hover reveal, under the description — the
   *  Timeline's per-column "(N new, M deprecated)" skill delta. */
  note?: ReactNode;
}) {
  const hasReveal = !!subtitle || note != null;
  return (
    <div className={`snap-grid__col-header${className ?? ''}`}>
      <strong className='snap-grid__col-header__name'>{display}</strong>
      {tag}
      {hasReveal ? (
        <span className='snap-grid__col-header__desc' aria-hidden='true'>
          {subtitle ? <span>{subtitle}</span> : null}
          {note != null ? (
            <span className='snap-grid__col-header__desc-note'>{note}</span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Proprietary-knowledge indicator. Rendered in its own thin grid column
 * to the left of the skill name so the marker reads at a glance and
 * doesn't compete with the truncation ellipsis at the right of the name.
 */
export function PropIndicator({ institutional }: { institutional?: boolean }) {
  if (!institutional) {
    return <span className='snap-grid__prop' aria-hidden='true' />;
  }
  // The marker itself is a CSS-drawn hollow circle (see
  // `.snap-grid__prop.is-proprietary::before`); the span just carries the
  // tooltip + grid slot.
  return (
    <span
      className='snap-grid__prop is-proprietary'
      title='Proprietary / institutional knowledge — not hireable externally'
    />
  );
}
